import { useEffect, useState } from "react";
import { CheckCircle2, Info } from "lucide-react";
import { apiClient } from "@/lib/api";
import { SEO, buildLegalServiceSchema } from "@/components/seo/SEO";
import { MotionReveal, MotionStagger, MotionStaggerItem } from "@/components/shared/MotionReveal";
import { Watermark } from "@/components/shared/Watermark";
import { DynamicIcon } from "@/components/shared/IconMap";
import { TestimonialsCarousel } from "@/components/home/TestimonialsCarousel";
import { PAGE_HERO_IMAGES } from "@/constants/firmInfo";
import { useLanguage } from "@/i18n/LanguageContext";

export default function CaseResults() {
  const [stats, setStats] = useState([]);
  const { t } = useLanguage();
  const highlights = t("caseResults.highlights");

  useEffect(() => {
    apiClient.get("/stats").then((res) => setStats(res.data)).catch(() => {});
  }, []);

  return (
    <>
      <SEO
        title={t("seo.caseResults.title")}
        description={t("seo.caseResults.description")}
        path="/case-results"
        jsonLd={buildLegalServiceSchema()}
      />

      <header className="relative bg-[color:var(--navy-950)] hero-noise overflow-hidden py-20 sm:py-24">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(11,18,32,0.93) 0%, rgba(11,18,32,0.86) 55%, rgba(11,18,32,0.97) 100%), url(${PAGE_HERO_IMAGES.caseResults})`,
            backgroundSize: "cover",
            backgroundPosition: "center 35%",
          }}
        />
        <Watermark word="Results" position="center" className="text-white" />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="text-xs font-medium tracking-[0.22em] uppercase text-[color:var(--gold-400)]">{t("caseResults.eyebrow")}</span>
          <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-white text-balance">
            {t("caseResults.heading1")} <span className="italic text-[color:var(--gold-400)]">{t("caseResults.headingEmphasis")}</span> {t("caseResults.headingEnd")}
          </h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto text-base sm:text-lg">
            {t("caseResults.subtitle")}
          </p>
        </div>
      </header>

      {stats.length > 0 && (
        <section
          className="relative py-16 sm:py-20 overflow-hidden"
          style={{ background: "linear-gradient(180deg, #0b1220 0%, #0F766E 22%, #0d5a54 100%)" }}
        >
          <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[color:var(--gold-500)]/70 to-transparent" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <MotionStagger className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              {stats.map((stat) => (
                <MotionStaggerItem key={stat.id}>
                  <div className="rounded-2xl bg-white/10 border border-[color:var(--gold-500)]/25 p-6 text-center h-full" data-testid="stat-card">
                    <div className="h-10 w-10 rounded-full bg-[color:var(--gold-500)]/15 ring-1 ring-[color:var(--gold-500)]/40 grid place-items-center mx-auto mb-3">
                      <DynamicIcon name={stat.icon} className="h-5 w-5 text-[color:var(--gold-400)]" />
                    </div>
                    <p className="text-3xl sm:text-4xl font-medium text-white font-heading">
                      {stat.value}{stat.suffix}
                    </p>
                    <p className="mt-1.5 text-xs sm:text-sm text-white/75">{t(`stats.labels.${stat.label}`, stat.label)}</p>
                  </div>
                </MotionStaggerItem>
              ))}
            </MotionStagger>
          </div>
        </section>
      )}

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <MotionReveal className="rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3 mb-10">
            <Info className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
            <p className="text-xs sm:text-sm text-amber-800 leading-relaxed" data-testid="case-results-disclaimer">
              {t("caseResults.disclaimer")}
            </p>
          </MotionReveal>

          <MotionStagger className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {highlights.map((h) => (
              <MotionStaggerItem key={h}>
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-[color:var(--sand)] p-4">
                  <CheckCircle2 className="h-5 w-5 text-[color:var(--gold-500)] shrink-0" />
                  <span className="text-sm text-slate-700 font-medium">{h}</span>
                </div>
              </MotionStaggerItem>
            ))}
          </MotionStagger>
        </div>
      </section>

      <TestimonialsCarousel />
    </>
  );
}
